import { createAsyncThunk } from "@reduxjs/toolkit";

import { environment } from "../../environments";
import {
  GetBaseItemRequest,
  GetBaseItemResponse,
  CreateBaseItemRequest,
  CreateBaseItemResponse,
  DeleteBaseItemRequest,
  DeleteBaseItemResponse,
  CreateProjectItemRequest,
  CreateProjectItemResponse,
  GetProjectItemsRequest,
  GetProjectItemsResponse,
  DeleteProjectItemRequest,
  DeleteProjectItemResponse,
  CreateProjectItemsHeaderResponse,
  CreateProjectItemsHeaderRequest,
  GetProjectItemsHeaderRequest,
  GetProjectItemsHeaderResponse,
} from "../../models/BaseItem.model";

const API_BASE_URL = environment.apiUrl;

// thunk לקבלת פריטים בסיסיים
export const getBaseItems = createAsyncThunk<
  GetBaseItemResponse,
  GetBaseItemRequest,
  { rejectValue: string }
>("baseItem/getBaseItems", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}BaseItem/GetBaseItems`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: GetBaseItemResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to get base items");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
});

// thunk ליצירת/עדכון פריט בסיסי
export const createOrUpdateItem = createAsyncThunk<
  CreateBaseItemResponse,
  CreateBaseItemRequest,
  { rejectValue: string }
>("baseItem/createOrUpdateItem", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}BaseItem/CreateOrUpdateBaseItem`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      }
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: CreateBaseItemResponse = await response.json();

    if (!data.success)
      return rejectWithValue(
        data.message || "Failed to create or update base item"
      );
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
});

// thunk למחיקת פריט בסיסי
export const deleteBaseItem = createAsyncThunk<
  DeleteBaseItemResponse,
  DeleteBaseItemRequest,
  { rejectValue: string }
>("baseItem/deleteBaseItem", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}BaseItem/DeleteBaseItem`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: DeleteBaseItemResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to delete base item");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
});

// thunk ליצירת/עדכון פריט פרוייקט
export const createOrUpdateProjectItem = createAsyncThunk<
  CreateProjectItemResponse,
  CreateProjectItemRequest,
  { rejectValue: string }
>(
  "baseItem/createOrUpdateProjectItem",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}BaseItem/CreateOrUpdeteProjectItem`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: CreateProjectItemResponse = await response.json();

      if (!data.success)
        return rejectWithValue(
          data.message || "Failed to create or update project item"
        );
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// thunk לקבלת פריטי פרוייקט
export const getProjectItems = createAsyncThunk<
  GetProjectItemsResponse,
  GetProjectItemsRequest,
  { rejectValue: string }
>("baseItem/getProjectItems", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}BaseItem/GetProjectItems`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: GetProjectItemsResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to get project items");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
});

// thunk למחיקת פריט פרוייקט
export const deleteProjectItem = createAsyncThunk<
  DeleteProjectItemResponse,
  DeleteProjectItemRequest,
  { rejectValue: string }
>("baseItem/deleteProjectItem", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}BaseItem/DeleteProjectItem`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: DeleteProjectItemResponse = await response.json();

    if (!data.success)
      return rejectWithValue(data.message || "Failed to delete project item");
    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error"
    );
  }
});

// thunk ליצירת כותרת פריטי פרוייקט
export const createProjectItemsHeader = createAsyncThunk<
  CreateProjectItemsHeaderResponse,
  CreateProjectItemsHeaderRequest,
  { rejectValue: string }
>(
  "baseItem/createProjectItemsHeader",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}BaseItem/CreateProjectItemsHeader`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: CreateProjectItemsHeaderResponse = await response.json();

      if (!data.success)
        return rejectWithValue(
          data.message || "Failed to create project items header"
        );
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);
export const getProjectItemsHeader = createAsyncThunk<
  GetProjectItemsHeaderResponse,
  GetProjectItemsHeaderRequest,
  { rejectValue: string }
>(
  "baseItem/getProjectItemsHeader",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}BaseItem/GetProjectItemsHeader`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: GetProjectItemsHeaderResponse = await response.json();

      if (!data.success)
        return rejectWithValue(
          data.message || "Failed to get project items header"
        );
      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);
